import { useCallback, useEffect, useRef, useState } from 'react';
import { convGet, convKeys, convRemove, convSet, convShared, convTouch, startConversationStorage } from './conversationStore';

// ─────────────────────────────────────────────────────────────────────────────
// useLiveAgent — 1:1 React port of the live-agent / WebSocket / heartbeat /
// polling logic from `ultimo-bots-widget/src/index.js`. All behavioural
// details (W-codes, debounces, polling cadences, session-token handling,
// rejoin races, 401 recovery) mirror the widget exactly.
//
// Consumers (Chat.js) provide:
//   • botId            — bot UUID
//   • sessionId        — current visitor session id (string)
//   • setSessionId     — setter to mint a new session on 401 / clear-chat
//   • appendMessage    — fn(text, sender)            — render an inline message
//   • appendSystemNotice — fn(text, opts)            — render a gray system pill
//   • removeWaitingNotice — fn()                     — remove waiting bubble
//   • markVisitorDelivered — fn(count: number)        — apply ✓ to last N
//   • markVisitorRead       — fn()                    — upgrade all to ✓✓
//   • setAgentTyping        — fn(boolean)             — show/hide typing pill
//   • injectWelcomeIfEmpty  — fn()                    — agent_joined branch
//   • saveChatHistory       — fn()                    — persist history snapshot
//   • clearAllMessages      — fn()                    — used by clear-chat
// ─────────────────────────────────────────────────────────────────────────────

const HTTP_BASE = process.env.REACT_APP_API_BASE_URL || '/api/';

function deriveWsUrl() {
  const env = process.env.REACT_APP_API_WEBSOCKET_URL;
  if (env) return env;
  if (typeof window === 'undefined') return '';
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  // In production, nginx exposes the WebSocket at `/api/ws` (proxied
  // internally to the backend's `/ws`). A bare `/ws` would hit the
  // React static-file location and fail. Use `/api/ws` as the safe
  // default so deployments work without REACT_APP_API_WEBSOCKET_URL.
  return `${proto}//${window.location.host}/api/ws`;
}

function generateSessionId() {
  const ts = new Date().getTime();
  return `${ts}-${Math.floor(Math.random() * 10000) + 1}`;
}

export function useLiveAgent(opts) {
  const {
    botId,
    sessionId,
    setSessionId,
    appendMessage,
    appendSystemNotice,
    removeWaitingNotice,
    markVisitorDelivered,
    markVisitorRead,
    setAgentTyping,
    injectWelcomeIfEmpty,
    saveChatHistory,
  } = opts;

  // ── Visible state ──
  const [liveSettings, setLiveSettings] = useState({
    show_request_button: false,
    request_button_text: 'Talk to a human',
    agent_avatar_url: null,
    agent_avatar_color: null,
    agent_display_name: null,
    offline_message: '',
  });
  const [agentAvailable, setAgentAvailable] = useState(false);
  const [liveSessionStatus, setLiveSessionStatus] = useState('active');
  const [currentAgentDisplayName, setCurrentAgentDisplayName] = useState(null);
  const [agentRequestPending, setAgentRequestPending] = useState(false);

  // ── Refs that mirror widget closures ──
  const sessionIdRef = useRef(sessionId);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  const liveSettingsRef = useRef(liveSettings);
  useEffect(() => { liveSettingsRef.current = liveSettings; }, [liveSettings]);

  const liveSessionStatusRef = useRef('active');
  const setStatus = useCallback((s) => {
    liveSessionStatusRef.current = s;
    setLiveSessionStatus(s);
  }, []);

  const currentAgentDisplayNameRef = useRef(null);
  const setAgentName = useCallback((n) => {
    currentAgentDisplayNameRef.current = n;
    setCurrentAgentDisplayName(n);
  }, []);

  const agentRequestPendingRef = useRef(false);
  const setReqPending = useCallback((b) => {
    agentRequestPendingRef.current = b;
    setAgentRequestPending(b);
  }, []);

  // Stable callback refs so timer/WS handlers always see the latest fns
  const cbRef = useRef({});
  useEffect(() => {
    cbRef.current = {
      appendMessage,
      appendSystemNotice,
      removeWaitingNotice,
      markVisitorDelivered,
      markVisitorRead,
      setAgentTyping,
      injectWelcomeIfEmpty,
      saveChatHistory,
    };
  });

  // Live-state refs (mirror widget `let` variables)
  const heartbeatTimerRef = useRef(null);
  const agentPollTimerRef = useRef(null);
  const lastAgentMessageIdRef = useRef(0);
  const liveHeartbeatStartedRef = useRef(false);
  const wsAuthenticatedRef = useRef(false);

  const liveWsRef = useRef(null);
  const wsConnectedRef = useRef(false);
  const wsConnectingRef = useRef(false);
  const wsReconnectDelayRef = useRef(1000);
  const wsReconnectTimerRef = useRef(null);
  // W16: consecutive widget_init token rejections (auth_error frames with
  // reason 'invalid session_token'). See the auth_error case for the policy.
  const wsAuthFailStreakRef = useRef(0);
  const visitorIsTypingRef = useRef(false);
  const joinAckSentRef = useRef(false);
  const lastJoinAckAtRef = useRef(0);
  const JOIN_ACK_DEBOUNCE_MS = 500;

  const botIdRef = useRef(botId);
  useEffect(() => { botIdRef.current = botId; }, [botId]);

  // ── Session token storage (W6), shared by the tabs with the conversation ──
  const getStoredSessionToken = useCallback(() => convGet(convKeys(botIdRef.current).token) || null, []);
  const setStoredSessionToken = useCallback((t) => {
    if (t) convSet(convKeys(botIdRef.current).token, t);
    else convRemove(convKeys(botIdRef.current).token);
  }, []);

  // The last agent message id is remembered with the conversation, so a reload
  // or another tab fetches only the agent messages it has not shown yet.
  useEffect(() => {
    if (!botId) return;
    lastAgentMessageIdRef.current = Math.max(lastAgentMessageIdRef.current, Number(convGet(convKeys(botId).agentMsg)) || 0);
  }, [botId]);
  const noteAgentMessageId = useCallback((id) => {
    if (!id || id <= lastAgentMessageIdRef.current) return;
    lastAgentMessageIdRef.current = id;
    convSet(convKeys(botIdRef.current).agentMsg, String(id));
  }, []);

  const effectiveAgentDisplayName = useCallback((fallback) => {
    return currentAgentDisplayNameRef.current
      || liveSettingsRef.current.agent_display_name
      || fallback
      || 'Agent';
  }, []);

  // Forward-declared helpers (assigned below). React-friendly pattern: stable
  // refs to functions so callees can call them without TDZ issues.
  const fnRef = useRef({});

  // ── Heartbeat / polling controls ──
  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const stopAgentPolling = useCallback(() => {
    if (agentPollTimerRef.current) {
      clearInterval(agentPollTimerRef.current);
      agentPollTimerRef.current = null;
    }
  }, []);

  const currentPollInterval = useCallback(() => {
    if (wsAuthenticatedRef.current) return 30000;
    if (wsConnectedRef.current) return 5000;
    return 2000;
  }, []);

  // ── 401 recovery: drop all live state, mint fresh session_id ──
  const resetLiveSessionForAuthFailure = useCallback(() => {
    setStoredSessionToken(null);
    stopHeartbeat();
    stopAgentPolling();
    liveHeartbeatStartedRef.current = false;
    setStatus('active');
    lastAgentMessageIdRef.current = 0;
    setReqPending(false);
    joinAckSentRef.current = false;
    wsAuthenticatedRef.current = false;
    if (liveWsRef.current) {
      try { liveWsRef.current.close(); } catch { /* ignore */ }
      liveWsRef.current = null;
    }
    wsConnectedRef.current = false;
    wsConnectingRef.current = false;
    setAgentName(null);
    // The old socket's close handler is ignored now (bound to its socket).
    visitorIsTypingRef.current = false;
    convRemove(convKeys(botIdRef.current).agentMsg);
    convRemove(convKeys(botIdRef.current).resume);
    const newSid = generateSessionId();
    convSet(convKeys(botIdRef.current).session, newSid);
    if (typeof setSessionId === 'function') setSessionId(newSid);
    sessionIdRef.current = newSid;
  }, [setStoredSessionToken, stopHeartbeat, stopAgentPolling, setStatus, setReqPending, setAgentName, setSessionId]);

  // ── State machine for liveSessionStatus transitions ──
  const setLiveSessionStatusFn = useCallback((newStatus) => {
    if (newStatus === 'agent_joined') {
      // Send join_ack on every agent-joined signal, even if no transition
      fnRef.current.sendJoinAck && fnRef.current.sendJoinAck();
    }
    if (newStatus === liveSessionStatusRef.current) return;

    const prev = liveSessionStatusRef.current;
    setStatus(newStatus);

    if (newStatus === 'agent_joined' && prev !== 'agent_joined') {
      // Remove waiting notice
      cbRef.current.removeWaitingNotice && cbRef.current.removeWaitingNotice();
      // Inject welcome messages if chat empty (rare path: rejoin on fresh load)
      cbRef.current.injectWelcomeIfEmpty && cbRef.current.injectWelcomeIfEmpty();

      const agentName = effectiveAgentDisplayName('A live agent');
      // Dedup: skip if the last system notice is a join already (page-reload
      // race, or another tab showed it under the bot-level fallback name)
      cbRef.current.appendSystemNotice && cbRef.current.appendSystemNotice(
        `${agentName} has joined the chat.`,
        { dedupKey: 'joined' },
      );
      try { cbRef.current.saveChatHistory && cbRef.current.saveChatHistory(); } catch { /* ignore */ }

      fnRef.current.startAgentPolling && fnRef.current.startAgentPolling();
      fnRef.current.sendJoinAck && fnRef.current.sendJoinAck();
    } else if (newStatus === 'active' && prev === 'agent_joined') {
      const agentName = effectiveAgentDisplayName('The live agent');
      cbRef.current.appendSystemNotice && cbRef.current.appendSystemNotice(
        `${agentName} has left the chat.`,
      );
      try { cbRef.current.saveChatHistory && cbRef.current.saveChatHistory(); } catch { /* ignore */ }
      stopAgentPolling();
      setReqPending(false);
      setAgentName(null);
      joinAckSentRef.current = false;
    } else if (newStatus === 'active' && prev === 'agent_requested') {
      cbRef.current.removeWaitingNotice && cbRef.current.removeWaitingNotice();
      cbRef.current.appendSystemNotice && cbRef.current.appendSystemNotice(
        'No agent responded in time. Your request was cancelled.',
      );
      const offlineMsg = liveSettingsRef.current.offline_message || '';
      if (offlineMsg) {
        cbRef.current.appendMessage && cbRef.current.appendMessage(offlineMsg, 'bot');
      }
      stopAgentPolling();
      setReqPending(false);
    }
  }, [setStatus, stopAgentPolling, setReqPending, setAgentName, effectiveAgentDisplayName]);

  // ── WebSocket: send / connect / reconnect ──
  const wsSend = useCallback((payload) => {
    const ws = liveWsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  const sendMessageAck = useCallback((messageIds) => {
    const sent = wsSend({ type: 'message_ack', session_id: sessionIdRef.current, message_ids: messageIds });
    if (!sent) {
      const tok = getStoredSessionToken();
      const body = { message_ids: messageIds };
      if (tok) body.session_token = tok;
      fetch(`${HTTP_BASE}live/ack/${sessionIdRef.current}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then((res) => {
        if (res && res.status === 401) resetLiveSessionForAuthFailure();
      }).catch(() => { /* best-effort */ });
    }
  }, [wsSend, getStoredSessionToken, resetLiveSessionForAuthFailure]);

  const sendJoinAck = useCallback(() => {
    const now = Date.now();
    if (now - lastJoinAckAtRef.current < JOIN_ACK_DEBOUNCE_MS) return;
    lastJoinAckAtRef.current = now;
    joinAckSentRef.current = true;
    const payload = { type: 'join_ack', session_id: sessionIdRef.current, bot_id: botIdRef.current };
    if (wsSend(payload)) return;
    fnRef.current.connectLiveWs && fnRef.current.connectLiveWs();
    let tries = 0;
    const retry = () => {
      tries += 1;
      if (wsSend(payload)) return;
      if (tries < 10) setTimeout(retry, 300);
    };
    setTimeout(retry, 300);
  }, [wsSend]);

  const restartAgentPollingWithCurrentInterval = useCallback(() => {
    if (!agentPollTimerRef.current) return;
    stopAgentPolling();
    fnRef.current.startAgentPolling && fnRef.current.startAgentPolling(false);
  }, [stopAgentPolling]);

  const scheduleWsReconnect = useCallback(() => {
    if (!liveSettingsRef.current.show_request_button) return;
    if (!liveHeartbeatStartedRef.current) return;
    if (wsReconnectTimerRef.current) return;
    wsAuthenticatedRef.current = false;
    const base = wsReconnectDelayRef.current;
    const jittered = Math.floor(base * (0.5 + Math.random()));
    wsReconnectTimerRef.current = setTimeout(() => {
      wsReconnectTimerRef.current = null;
      wsReconnectDelayRef.current = Math.min(wsReconnectDelayRef.current * 2, 30000);
      fnRef.current.connectLiveWs && fnRef.current.connectLiveWs();
    }, jittered);
  }, []);

  const connectLiveWs = useCallback(() => {
    if (wsConnectingRef.current) return;
    const existing = liveWsRef.current;
    if (existing && (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)) return;
    if (wsReconnectTimerRef.current) {
      clearTimeout(wsReconnectTimerRef.current);
      wsReconnectTimerRef.current = null;
    }

    wsConnectingRef.current = true;
    let ws;
    try {
      ws = new WebSocket(deriveWsUrl());
    } catch (err) {
      wsConnectingRef.current = false;
      console.error('WS constructor error:', err);
      scheduleWsReconnect();
      return;
    }
    liveWsRef.current = ws;
    // The token this socket's widget_init carried (see auth_error).
    let initToken = null;

    // Every handler belongs to this socket only: once liveWsRef points
    // elsewhere (chat cleared, session reset, another tab's conversation), a
    // late open/message/close of this one is ignored instead of dropping the
    // newer socket and reconnecting again.
    ws.addEventListener('open', () => {
      if (liveWsRef.current !== ws) return;
      wsConnectingRef.current = false;
      wsConnectedRef.current = true;
      // W16: do NOT reset the backoff here. A 4401 auth rejection closes the
      // socket AFTER a successful open, so resetting on open would pin the
      // reconnect delay at ~1s forever (widget_init → 4401 → reconnect loop
      // hammering the backend). The backoff resets on widget_init_ack instead.
      initToken = getStoredSessionToken();
      ws.send(JSON.stringify({
        type: 'widget_init',
        session_id: sessionIdRef.current,
        bot_id: botIdRef.current,
        session_token: initToken,
      }));
      restartAgentPollingWithCurrentInterval();
    });

    ws.addEventListener('message', (ev) => {
      if (liveWsRef.current !== ws) return;
      let event;
      try { event = JSON.parse(ev.data); } catch { return; }

      switch (event.type) {
        case 'widget_init_ack': {
          wsAuthenticatedRef.current = true;
          // W16: handshake succeeded — NOW reset the reconnect backoff and
          // the token-rejection streak (not on 'open', see comment there).
          wsReconnectDelayRef.current = 1000;
          wsAuthFailStreakRef.current = 0;
          restartAgentPollingWithCurrentInterval();
          if (event.session_status === 'agent_joined') {
            if (event.agent_display_name) setAgentName(event.agent_display_name);
            if (liveSessionStatusRef.current !== 'agent_joined') {
              setLiveSessionStatusFn('agent_joined');
            }
            joinAckSentRef.current = false;
            sendJoinAck();
          } else if (event.session_status && event.session_status !== 'agent_joined') {
            if (event.session_status === 'agent_requested') fnRef.current.restoreWaitingForAgent && fnRef.current.restoreWaitingForAgent();
            if (liveSessionStatusRef.current === 'agent_joined' || liveSessionStatusRef.current === 'agent_requested') {
              setLiveSessionStatusFn(event.session_status === 'agent_requested' ? 'agent_requested' : 'active');
            }
          }
          break;
        }
        case 'live_agent_message': {
          if (event.id && event.id <= lastAgentMessageIdRef.current) break;
          if (!event.content) break;
          cbRef.current.setAgentTyping && cbRef.current.setAgentTyping(false);
          // Remembered before the history is saved: a reload never shows it twice.
          noteAgentMessageId(event.id);
          cbRef.current.appendMessage && cbRef.current.appendMessage(event.content, 'agent');
          if (event.id) sendMessageAck([event.id]);
          break;
        }
        case 'live_agent_joined': {
          if (event.agent_display_name) setAgentName(event.agent_display_name);
          joinAckSentRef.current = false;
          if (liveSessionStatusRef.current !== 'agent_joined') {
            setLiveSessionStatusFn('agent_joined');
          } else {
            sendJoinAck();
          }
          break;
        }
        case 'live_agent_left': {
          setLiveSessionStatusFn('active');
          break;
        }
        case 'live_agent_requested': {
          if (liveSessionStatusRef.current !== 'agent_requested' && liveSessionStatusRef.current !== 'agent_joined') {
            fnRef.current.showWaitingForAgentNotice && fnRef.current.showWaitingForAgentNotice();
          }
          break;
        }
        case 'typing_start': {
          cbRef.current.setAgentTyping && cbRef.current.setAgentTyping(true);
          break;
        }
        case 'typing_stop': {
          cbRef.current.setAgentTyping && cbRef.current.setAgentTyping(false);
          break;
        }
        case 'message_delivered': {
          if (event.direction === 'visitor') {
            const count = Array.isArray(event.message_ids) ? event.message_ids.length : 1;
            cbRef.current.markVisitorDelivered && cbRef.current.markVisitorDelivered(count);
          }
          break;
        }
        case 'messages_read': {
          cbRef.current.markVisitorRead && cbRef.current.markVisitorRead();
          break;
        }
        case 'ping': {
          wsSend({ type: 'pong' });
          break;
        }
        case 'auth_error': {
          // W16: the backend rejected our widget_init session_token and is
          // about to close the socket with 4401. Without this case the close
          // is treated as a transient disconnect and the client reconnects
          // forever (~1s cadence), never healing. Two situations:
          //   - the token we SENT was rejected and is still the one we hold →
          //     it is stale; retrying can never fix it. Reset immediately
          //     (rotates session_id + drops the token, same recovery as an
          //     HTTP 401).
          //   - we sent NO token, or another one has arrived since (the
          //     first-heartbeat response, ours or another tab's) → the next
          //     backoff reconnect will carry it. Judging by the token held
          //     NOW started a needless new session (and a second "new chat"
          //     notification). Only reset after the 3rd consecutive rejection.
          // Other auth_error reasons (e.g. a transient Redis lookup failure
          // server-side) fall through to the normal backoff reconnect.
          if (event.reason === 'invalid session_token') {
            wsAuthFailStreakRef.current += 1;
            if ((initToken && initToken === getStoredSessionToken()) || wsAuthFailStreakRef.current >= 3) {
              wsAuthFailStreakRef.current = 0;
              resetLiveSessionForAuthFailure();
            }
          }
          break;
        }
        default: break;
      }
    });

    ws.addEventListener('close', () => {
      if (liveWsRef.current !== ws) return;
      wsConnectingRef.current = false;
      wsConnectedRef.current = false;
      wsAuthenticatedRef.current = false;
      visitorIsTypingRef.current = false;
      liveWsRef.current = null;
      restartAgentPollingWithCurrentInterval();
      scheduleWsReconnect();
    });

    ws.addEventListener('error', () => {
      if (liveWsRef.current !== ws) return;
      wsConnectingRef.current = false;
      wsAuthenticatedRef.current = false;
    });
  }, [getStoredSessionToken, restartAgentPollingWithCurrentInterval, scheduleWsReconnect, setAgentName, setLiveSessionStatusFn, sendJoinAck, sendMessageAck, wsSend, resetLiveSessionForAuthFailure, noteAgentMessageId]);

  // ── Heartbeat ──
  const sendHeartbeat = useCallback(async () => {
    // Skip if session_id / bot_id not yet known (would 400 the backend).
    if (!sessionIdRef.current || !botIdRef.current) return;
    try {
      const storedToken = getStoredSessionToken();
      const body = {
        session_id: sessionIdRef.current,
        bot_id: botIdRef.current,
        visitor_name: 'Website Visitor',
        page_url: window.location.href,
        platform: 'chat_link',
        visibility: document.visibilityState === 'hidden' ? 'hidden' : 'visible',
      };
      if (storedToken) body.session_token = storedToken;
      if (convShared() && !convGet(convKeys(botIdRef.current).resume)) body.want_resume_secret = true;
      const res = await fetch(`${HTTP_BASE}live/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        resetLiveSessionForAuthFailure();
        return;
      }
      if (res.ok) {
        const data = await res.json();
        if (data && data.session_token) setStoredSessionToken(data.session_token);
        if (data && data.resume_secret) convSet(convKeys(botIdRef.current).resume, data.resume_secret);
        if (data.agent_display_name) setAgentName(data.agent_display_name);
        if (data.session_status === 'agent_joined' || data.session_status === 'active') {
          setLiveSessionStatusFn(data.session_status);
        } else if (data.session_status === 'agent_requested') {
          fnRef.current.restoreWaitingForAgent && fnRef.current.restoreWaitingForAgent();
        }
        convTouch(botIdRef.current);
        if (data.session_status === 'agent_joined' && !agentPollTimerRef.current) {
          fnRef.current.startAgentPolling && fnRef.current.startAgentPolling();
        }
      }
    } catch { /* best-effort */ }
  }, [getStoredSessionToken, resetLiveSessionForAuthFailure, setLiveSessionStatusFn, setStoredSessionToken, setAgentName]);

  const startHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) return;
    try { fnRef.current.connectLiveWs && fnRef.current.connectLiveWs(); } catch { /* ignore */ }
    sendHeartbeat();
    heartbeatTimerRef.current = setInterval(sendHeartbeat, 15000);
  }, [sendHeartbeat]);

  const startSlowHiddenHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    heartbeatTimerRef.current = setInterval(sendHeartbeat, 60000);
  }, [sendHeartbeat]);

  // ── Polling /api/live/messages ──
  const pollAgentMessages = useCallback(async () => {
    try {
      const tok = getStoredSessionToken();
      const url = `${HTTP_BASE}live/messages/${sessionIdRef.current}?after_id=${lastAgentMessageIdRef.current}`
        + (tok ? `&session_token=${encodeURIComponent(tok)}` : '');
      const res = await fetch(url);
      if (res.status === 401) {
        resetLiveSessionForAuthFailure();
        return;
      }
      if (!res.ok) return;
      const data = await res.json();
      if (data.agent_display_name) setAgentName(data.agent_display_name);
      if (data.status === 'agent_joined' || data.status === 'active') {
        const prev = liveSessionStatusRef.current;
        setLiveSessionStatusFn(data.status);
        if (data.status === 'active' && (prev === 'agent_joined' || prev === 'agent_requested')) {
          return;
        }
      }
      const messages = data.messages || [];
      for (const msg of messages) {
        if (msg.id && msg.id <= lastAgentMessageIdRef.current) continue;
        noteAgentMessageId(msg.id);
        if (msg.type === 'agent' && msg.content) {
          cbRef.current.appendMessage && cbRef.current.appendMessage(msg.content, 'agent');
        }
      }
    } catch { /* non-fatal */ }
  }, [getStoredSessionToken, resetLiveSessionForAuthFailure, setLiveSessionStatusFn, noteAgentMessageId, setAgentName]);

  const startAgentPolling = useCallback(async (connectWs = true) => {
    if (agentPollTimerRef.current) return;
    if (connectWs) connectLiveWs();
    if (lastAgentMessageIdRef.current === 0) {
      try {
        const tok = getStoredSessionToken();
        const seedUrl = `${HTTP_BASE}live/messages/${sessionIdRef.current}?after_id=0`
          + (tok ? `&session_token=${encodeURIComponent(tok)}` : '');
        const seedRes = await fetch(seedUrl);
        if (seedRes.status === 401) {
          resetLiveSessionForAuthFailure();
          return;
        }
        if (seedRes.ok) {
          const seedData = await seedRes.json();
          const msgs = seedData.messages || [];
          if (msgs.length > 0) {
            noteAgentMessageId(msgs[msgs.length - 1].id || 0);
          }
          if (seedData.status && seedData.status !== liveSessionStatusRef.current) {
            setLiveSessionStatusFn(seedData.status);
          }
        }
      } catch { /* best-effort */ }
    }
    if (agentPollTimerRef.current) return;
    pollAgentMessages();
    agentPollTimerRef.current = setInterval(pollAgentMessages, currentPollInterval());
  }, [connectLiveWs, getStoredSessionToken, resetLiveSessionForAuthFailure, setLiveSessionStatusFn, pollAgentMessages, currentPollInterval, noteAgentMessageId]);

  // ── Waiting-for-agent UI helper ──
  const showWaitingForAgentNotice = useCallback(() => {
    setStatus('agent_requested');
    setReqPending(true);
    cbRef.current.appendSystemNotice && cbRef.current.appendSystemNotice(
      'You requested a live agent. Please wait',
      {
        waiting: true,
        dedupKey: 'waiting_for_agent',
      },
    );
    startAgentPolling();
  }, [setStatus, setReqPending, startAgentPolling]);

  // Wire forward-declared fn pointers
  useEffect(() => {
    fnRef.current.connectLiveWs = connectLiveWs;
    fnRef.current.sendJoinAck = sendJoinAck;
    fnRef.current.startAgentPolling = startAgentPolling;
    fnRef.current.showWaitingForAgentNotice = showWaitingForAgentNotice;
  });

  // ── Public actions ──
  const requestAgent = useCallback(async () => {
    if (agentRequestPendingRef.current) return;
    if (liveSessionStatusRef.current === 'agent_joined') return;
    setReqPending(true);
    try {
      const availRes = await fetch(`${HTTP_BASE}live/agent_available/${botIdRef.current}`);
      if (availRes.ok) {
        const availData = await availRes.json();
        if (!availData.available) {
          cbRef.current.appendSystemNotice && cbRef.current.appendSystemNotice(
            'No agent available. Please use the AI assistant.',
          );
          setReqPending(false);
          return;
        }
      }
      if (!liveHeartbeatStartedRef.current) {
        liveHeartbeatStartedRef.current = true;
        await sendHeartbeat();
        startHeartbeat();
      }
      let res = await fetch(`${HTTP_BASE}live/request_agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          bot_id: botIdRef.current,
          session_token: getStoredSessionToken(),
        }),
      });
      if (res.status === 400 || res.status === 401) {
        setStoredSessionToken(null);
        // The old session's ids go with it, so the new one gets its own resume secret.
        convRemove(convKeys(botIdRef.current).agentMsg);
        convRemove(convKeys(botIdRef.current).resume);
        lastAgentMessageIdRef.current = 0;
        const newSid = generateSessionId();
        convSet(convKeys(botIdRef.current).session, newSid);
        if (typeof setSessionId === 'function') setSessionId(newSid);
        sessionIdRef.current = newSid;
        try { await sendHeartbeat(); } catch { /* non-fatal */ }
        res = await fetch(`${HTTP_BASE}live/request_agent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            bot_id: botIdRef.current,
            session_token: getStoredSessionToken(),
          }),
        });
      }
      if (res.ok) {
        showWaitingForAgentNotice();
      } else {
        setReqPending(false);
      }
    } catch (err) {
      console.error('Request agent failed:', err);
      setReqPending(false);
    }
  }, [setReqPending, sendHeartbeat, startHeartbeat, getStoredSessionToken, setStoredSessionToken, setSessionId, showWaitingForAgentNotice]);

  const cancelRequest = useCallback(async () => {
    try {
      const tok = getStoredSessionToken();
      const cancelBody = { session_id: sessionIdRef.current };
      if (tok) cancelBody.session_token = tok;
      const cancelRes = await fetch(`${HTTP_BASE}live/cancel_request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cancelBody),
      });
      if (cancelRes && cancelRes.status === 401) {
        resetLiveSessionForAuthFailure();
        return;
      }
    } catch { /* best-effort */ }
    cbRef.current.removeWaitingNotice && cbRef.current.removeWaitingNotice();
    setStatus('active');
    setReqPending(false);
    stopAgentPolling();
    cbRef.current.appendSystemNotice && cbRef.current.appendSystemNotice('Agent request cancelled.');
  }, [getStoredSessionToken, resetLiveSessionForAuthFailure, setStatus, setReqPending, stopAgentPolling]);

  const disconnectAgent = useCallback(async () => {
    try {
      const tok = getStoredSessionToken();
      const cancelBody = { session_id: sessionIdRef.current };
      if (tok) cancelBody.session_token = tok;
      const cancelRes = await fetch(`${HTTP_BASE}live/cancel_request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cancelBody),
      });
      if (cancelRes && cancelRes.status === 401) {
        resetLiveSessionForAuthFailure();
        return;
      }
    } catch { /* non-fatal */ }
    if (liveSessionStatusRef.current === 'agent_joined') {
      setLiveSessionStatusFn('active');
    }
  }, [getStoredSessionToken, resetLiveSessionForAuthFailure, setLiveSessionStatusFn]);

  const isLiveActive = useCallback(() => {
    return liveSessionStatusRef.current === 'agent_requested'
      || liveSessionStatusRef.current === 'agent_joined';
  }, []);

  const ensureHeartbeatStarted = useCallback(() => {
    if (!liveHeartbeatStartedRef.current) {
      liveHeartbeatStartedRef.current = true;
      startHeartbeat();
    } else if (!heartbeatTimerRef.current) {
      // stopped by pagehide without an unload, or by a hidden tab
      startHeartbeat();
    }
  }, [startHeartbeat]);

  // After a reload or in another tab the visitor is still waiting for an
  // agent: show the waiting notice again and watch for the join.
  const restoreWaitingForAgent = useCallback(() => {
    if (liveSessionStatusRef.current !== 'active') return;
    fnRef.current.showWaitingForAgentNotice && fnRef.current.showWaitingForAgentNotice();
    fnRef.current.startAgentPolling && fnRef.current.startAgentPolling();
  }, []);
  fnRef.current.restoreWaitingForAgent = restoreWaitingForAgent;

  // Replies the team wrote while the visitor was away (also after the chat
  // ended): fetched with the conversation's resume secret. The caller renders
  // them, then calls ackFollowUps.
  const fetchFollowUps = useCallback(async () => {
    const k = convKeys(botIdRef.current);
    const secret = convGet(k.resume);
    if (!secret || !sessionIdRef.current) return [];
    try {
      const res = await fetch(`${HTTP_BASE}live/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          bot_id: botIdRef.current,
          resume_secret: secret,
          after_id: lastAgentMessageIdRef.current,
        }),
      });
      if (res.status === 401 || res.status === 410) {
        convRemove(k.resume);
        return [];
      }
      if (!res.ok) return [];
      const data = await res.json();
      if (data.session_token) setStoredSessionToken(data.session_token);
      return (data.messages || []).filter((m) => m.id > lastAgentMessageIdRef.current && m.content);
    } catch {
      return [];
    }
  }, [setStoredSessionToken]);

  // The caller rendered them: remember their ids, then ack (delivered).
  const ackFollowUps = useCallback((ids) => {
    if (!ids || !ids.length) return;
    ids.forEach((id) => noteAgentMessageId(id));
    sendMessageAck(ids);
  }, [noteAgentMessageId, sendMessageAck]);

  // Another tab started or adopted a different conversation: drop this tab's
  // live state without ending anything on the server (the caller shows the
  // other conversation and restarts the heartbeat if it has a chat).
  const followConversation = useCallback(() => {
    stopHeartbeat();
    stopAgentPolling();
    cbRef.current.setAgentTyping && cbRef.current.setAgentTyping(false);
    const ws = liveWsRef.current;
    liveWsRef.current = null;
    if (ws) { try { ws.close(); } catch { /* ignore */ } }
    if (wsReconnectTimerRef.current) {
      clearTimeout(wsReconnectTimerRef.current);
      wsReconnectTimerRef.current = null;
    }
    wsConnectedRef.current = false;
    wsConnectingRef.current = false;
    wsAuthenticatedRef.current = false;
    setStatus('active');
    setReqPending(false);
    joinAckSentRef.current = false;
    setAgentName(null);
    visitorIsTypingRef.current = false;
    liveHeartbeatStartedRef.current = false;
    lastAgentMessageIdRef.current = Number(convGet(convKeys(botIdRef.current).agentMsg)) || 0;
  }, [stopHeartbeat, stopAgentPolling, setStatus, setReqPending, setAgentName]);

  // Send visitor message when in live mode (fire-and-forget so backend
  // persists it and pushes to portal). Also sends typing_stop.
  const sendVisitorMessageLive = useCallback((text) => {
    const url = `${HTTP_BASE}chatbot_response?`
      + `user_input=${encodeURIComponent(text)}`
      + `&session_id=${sessionIdRef.current}&bot_id=${botIdRef.current}&language=english&platform=chat_link`;
    fetch(url).catch(() => { /* best-effort */ });
    if (visitorIsTypingRef.current) {
      visitorIsTypingRef.current = false;
      wsSend({ type: 'typing_stop', session_id: sessionIdRef.current });
    }
  }, [wsSend]);

  // Visitor typing — only meaningful when an agent is connected.
  const notifyTyping = useCallback((isTypingNow) => {
    if (!isLiveActive()) return;
    if (isTypingNow && !visitorIsTypingRef.current) {
      visitorIsTypingRef.current = true;
      wsSend({ type: 'typing_start', session_id: sessionIdRef.current });
    } else if (!isTypingNow && visitorIsTypingRef.current) {
      visitorIsTypingRef.current = false;
      wsSend({ type: 'typing_stop', session_id: sessionIdRef.current });
    }
  }, [isLiveActive, wsSend]);

  // Clear-chat lifecycle (W17): stop everything, call /api/live/disconnect
  // (fetch+keepalive+omit so CORS:* allows it), reset state, mint new sid.
  // The new sid is generated by Chat.js and passed in via setSessionId effect.
  const resetLiveSession = useCallback(() => {
    stopHeartbeat();
    stopAgentPolling();
    cbRef.current.setAgentTyping && cbRef.current.setAgentTyping(false);
    if (liveWsRef.current) {
      try { liveWsRef.current.close(); } catch { /* ignore */ }
      liveWsRef.current = null;
    }
    wsConnectedRef.current = false;
    wsConnectingRef.current = false;
    setStatus('active');
    lastAgentMessageIdRef.current = 0;
    setReqPending(false);
    joinAckSentRef.current = false;
    setAgentName(null);
    visitorIsTypingRef.current = false;

    if (liveHeartbeatStartedRef.current) {
      try {
        const tok = getStoredSessionToken();
        const payload = { session_id: sessionIdRef.current, bot_id: botIdRef.current };
        if (tok) payload.session_token = tok;
        fetch(`${HTTP_BASE}live/disconnect`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'omit',
          keepalive: true,
        }).catch(() => { /* non-fatal */ });
      } catch { /* non-fatal */ }
    }
    setStoredSessionToken(null);
    convRemove(convKeys(botIdRef.current).agentMsg);
    convRemove(convKeys(botIdRef.current).resume);
    wsAuthenticatedRef.current = false;
    liveHeartbeatStartedRef.current = false;
  }, [stopHeartbeat, stopAgentPolling, setStatus, setReqPending, setAgentName, getStoredSessionToken, setStoredSessionToken]);

  // ── Initial fetch: live settings + agent availability + restore session ──
  useEffect(() => {
    if (!botId) return;
    let cancelled = false;
    (async () => {
      try {
        const [settingsRes, availRes] = await Promise.all([
          fetch(`${HTTP_BASE}live_chat_settings_public/${botId}`, { cache: 'no-store' }),
          fetch(`${HTTP_BASE}live/agent_available/${botId}`),
        ]);
        if (cancelled) return;
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          if (!cancelled) {
            setLiveSettings({
              show_request_button: !!settings.show_request_button,
              request_button_text: settings.request_button_text || 'Talk to a human',
              agent_avatar_url: settings.agent_avatar_url || null,
              agent_avatar_color: settings.agent_avatar_color || null,
              agent_display_name: settings.agent_display_name || null,
              offline_message: settings.offline_message || '',
            });
          }
        }
        if (availRes.ok) {
          const data = await availRes.json();
          if (!cancelled) setAgentAvailable(!!data.available);
        }
      } catch { /* best-effort */ }
    })();

    // Resume heartbeat ONLY if a real conversation exists for this bot.
    // Chat.js auto-persists the welcome bubble(s) to sessionStorage as soon
    // as the page loads, so a bare `chat-history-{botId}` key is NOT a
    // reliable "ongoing chat" signal: a plain refresh would otherwise spin
    // up a live_session row on the backend and fire a `new_chat`
    // notification even though the visitor never wrote anything. Require
    // at least one visitor message (sender === 'user') OR an existing live
    // session token before re-arming the heartbeat. Wait until sessionId
    // is populated (Chat.js sets it in a separate effect) before firing
    // the first beat, otherwise the backend 400s.
    try {
      // Back after a break: the chat page decides (fetchFollowUps) whether the
      // old conversation continues; it starts the heartbeat then.
      if (startConversationStorage(botId).resumeOnly) return () => { cancelled = true; };
      const raw = convGet(convKeys(botIdRef.current).history);
      let hasVisitorMessage = false;
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            hasVisitorMessage = parsed.some((m) => m && m.sender === 'user');
          }
        } catch { /* corrupt entry — treat as no history */ }
      }
      const hasLiveToken = !!getStoredSessionToken();
      if (hasVisitorMessage || hasLiveToken) {
        liveHeartbeatStartedRef.current = true;
        const tryStart = () => {
          if (cancelled) return;
          if (sessionIdRef.current) startHeartbeat();
          else setTimeout(tryStart, 50);
        };
        tryStart();
      }
    } catch { /* ignore */ }

    return () => { cancelled = true; };
  }, [botId, startHeartbeat, getStoredSessionToken]);

  // Re-poll agent availability every 30s while CTA may be shown
  useEffect(() => {
    if (!botId) return;
    const id = setInterval(async () => {
      try {
        const res = await fetch(`${HTTP_BASE}live/agent_available/${botId}`);
        if (!res.ok) return;
        const data = await res.json();
        setAgentAvailable(!!data.available);
      } catch { /* ignore */ }
    }, 30000);
    return () => clearInterval(id);
  }, [botId]);

  // ── Tab visibility: pause polling, slow heartbeat, restart on focus ──
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stopAgentPolling();
        // Keep a slow 60 s heartbeat in every mode and report the hidden tab at
        // once: the inbox shows "in the background" instead of "connection
        // lost", and the chat does not end while the visitor reads another tab.
        if (liveHeartbeatStartedRef.current) {
          sendHeartbeat();
          startSlowHiddenHeartbeat();
        }
      } else {
        if (liveHeartbeatStartedRef.current) {
          if (heartbeatTimerRef.current) {
            clearInterval(heartbeatTimerRef.current);
            heartbeatTimerRef.current = null;
          }
          startHeartbeat();
        }
        if (
          liveHeartbeatStartedRef.current
          && (!liveWsRef.current || liveWsRef.current.readyState === WebSocket.CLOSED)
        ) {
          scheduleWsReconnect();
        }
        if (
          liveSessionStatusRef.current === 'agent_requested'
          || liveSessionStatusRef.current === 'agent_joined'
        ) {
          startAgentPolling(false);
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [stopAgentPolling, startSlowHiddenHeartbeat, sendHeartbeat, startHeartbeat, scheduleWsReconnect, startAgentPolling]);

  // pagehide: close WS cleanly (do NOT call /api/live/disconnect — see widget
  // rationale). Not beforeunload: that also fires for a download link or a
  // cancelled "leave this page?" dialog, and the heartbeat then stayed dead.
  // pageshow from the back/forward cache resumes it.
  useEffect(() => {
    const onPageHide = () => {
      stopHeartbeat();
      if (liveHeartbeatStartedRef.current && liveWsRef.current && liveWsRef.current.readyState === WebSocket.OPEN) {
        try { liveWsRef.current.close(); } catch { /* ignore */ }
      }
    };
    const onPageShow = (event) => {
      if (event.persisted && liveHeartbeatStartedRef.current) startHeartbeat();
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [stopHeartbeat, startHeartbeat]);

  // Cleanup all timers/WS on unmount
  useEffect(() => {
    return () => {
      stopHeartbeat();
      stopAgentPolling();
      if (wsReconnectTimerRef.current) {
        clearTimeout(wsReconnectTimerRef.current);
        wsReconnectTimerRef.current = null;
      }
      if (liveWsRef.current) {
        try { liveWsRef.current.close(); } catch { /* ignore */ }
        liveWsRef.current = null;
      }
    };
  }, [stopHeartbeat, stopAgentPolling]);

  return {
    // state
    liveSettings,
    agentAvailable,
    liveSessionStatus,
    currentAgentDisplayName,
    agentRequestPending,
    // helpers
    effectiveAgentDisplayName,
    isLiveActive,
    // actions
    requestAgent,
    cancelRequest,
    disconnectAgent,
    sendVisitorMessageLive,
    ensureHeartbeatStarted,
    notifyTyping,
    resetLiveSession,
    fetchFollowUps,
    ackFollowUps,
    followConversation,
  };
}

export { generateSessionId };
